<template>
  <div class="directory-view">
    <div class="dir-header">Company Directory</div>
    <div class="page-sub">All companies across projects</div>

    <div class="toolbar">
      <input v-model="quickFilter" placeholder="Search companies..." />
      <div class="toolbar-actions">
        <button @click="refreshStats" class="refresh-btn" :disabled="isLoading" title="Refresh company statistics">
          🔄 Refresh Stats
        </button>
        <button @click="openAddCompanyModal" class="add-company-btn">
          + Add New Company
        </button>
      </div>
    </div>

    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner">🔄</div>
      <div>Loading companies...</div>
    </div>
    
    <div v-else-if="rows.length" class="grid-card">
      <div class="grid-header">
        <span>{{ totalRecords.toLocaleString() }} companies</span>
        <span v-if="optimizedEndpointsAvailable" class="optimized-badge">⚡ Optimized</span>
      </div>
      <AgGrid
        class="ag-theme-alpine directory-grid"
        :row-data="rows"
        :column-defs="columnDefs"
        :row-height="36"
        @grid-ready="onGridReady"
      />
    </div>
    
    <div v-else class="empty-state">
      <div>No companies found</div>
      <button @click="loadCompaniesOptimized" class="retry-btn">Retry</button>
    </div>

    <!-- Add New Company Modal -->
    <div v-if="showAddCompanyModal" class="modal-backdrop" @click="closeAddCompanyModal">
      <div class="add-company-modal" @click.stop>
        <div class="modal-header">
          <h3>Add New Company</h3>
          <button class="close-button" @click="closeAddCompanyModal">×</button>
        </div>
        
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group">
              <label>Company Name *</label>
              <input v-model="companyForm.name" type="text" class="form-input" placeholder="Enter company name" />
            </div>
            <div class="form-group">
              <label>Company Type *</label>
              <select v-model="companyForm.company_type" class="form-input" @change="updateCompanyType">
                <option value="">Select Type</option>
                <optgroup label="Contractors">
                  <option value="General Contractor">General Contractor</option>
                  <option value="Trade Contractor">Trade Contractor</option>
                  <option value="Specialty Contractor">Specialty Contractor</option>
                  <option value="Subcontractor">Subcontractor</option>
                </optgroup>
                <optgroup label="Investors/Lenders/Developers">
                  <option value="Debt">Lender</option>
                  <option value="Equity">Investor</option>
                  <option value="Developer(s)">Developer</option>
                </optgroup>
                <optgroup label="Consultants">
                  <option value="Design Architect">Design Architect</option>
                  <option value="Architect of Record">Architect of Record</option>
                  <option value="Civil Engineer">Civil Engineer</option>
                  <option value="Structural Engineer">Structural Engineer</option>
                  <option value="MEP Engineer">MEP Engineer</option>
                  <option value="Geotechnical Engineer">Geotechnical Engineer</option>
                  <option value="Environmental Engineer">Environmental Engineer</option>
                  <option value="Interior Design">Interior Designer</option>
                  <option value="Landscape Architect">Landscape Architect</option>
                  <option value="Owner's Representative">Owner's Representative</option>
                </optgroup>
              </select>
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label>Contact Email</label>
              <input v-model="companyForm.email" type="email" class="form-input" placeholder="contact@company.com" />
            </div>
            <div class="form-group">
              <label>Phone Number</label>
              <input v-model="companyForm.phone" type="tel" class="form-input" placeholder="(555) 123-4567" />
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label>Address</label>
              <input v-model="companyForm.address" type="text" class="form-input" placeholder="Street address" />
            </div>
            <div class="form-group">
              <label>City</label>
              <input v-model="companyForm.city" type="text" class="form-input" placeholder="City" />
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label>State</label>
              <input v-model="companyForm.state" type="text" class="form-input" placeholder="State" />
            </div>
            <div class="form-group">
              <label>Zip Code</label>
              <input v-model="companyForm.zipcode" type="text" class="form-input" placeholder="12345" />
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label>License Number</label>
              <input v-model="companyForm.license_number" type="text" class="form-input" placeholder="License #" />
            </div>
            <div class="form-group">
              <label>Website</label>
              <input v-model="companyForm.website" type="url" class="form-input" placeholder="https://company.com" />
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group full-width">
              <label>Notes</label>
              <textarea v-model="companyForm.notes" class="form-textarea" rows="3" placeholder="Additional notes about the company..."></textarea>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeAddCompanyModal">Cancel</button>
          <button class="btn-primary" @click="saveNewCompany" :disabled="!companyForm.name || !companyForm.company_type">
            Save Company
          </button>
        </div>
      </div>
    </div>
  </div>
  </template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import AgGrid from '@/components/AgGrid.vue'
import { useAuthStore } from '@/stores/auth_store'
import { useErrorStore } from '@/stores/error_store'
import { axiosQuery, axiosPostQuery } from '@/assets/js/utils'

const authStore = useAuthStore()
const errorStore = useErrorStore()
const router = useRouter()

const apiBase = process.env.VUE_APP_ENV_API_BASE || 'http://167.172.25.255:3000/api/v1/'
const quickFilter = ref('')
const rows = ref([])
const gridApi = ref(null)

// Add Company Modal
const showAddCompanyModal = ref(false)
const companyForm = ref({
  name: '',
  company_type: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zipcode: '',
  license_number: '',
  website: '',
  notes: ''
})

// Company type configuration
const COMPANY_TYPE_CONFIG = {
  'General Contractor': { apiEndpoint: 'contractors', routeType: 'contractors', category: 'Contractor' },
  'Trade Contractor': { apiEndpoint: 'contractors', routeType: 'contractors', category: 'Contractor' },
  'Specialty Contractor': { apiEndpoint: 'contractors', routeType: 'contractors', category: 'Contractor' },
  'Subcontractor': { apiEndpoint: 'contractors', routeType: 'contractors', category: 'Contractor' },
  'Debt': { apiEndpoint: 'investor_sponsors', routeType: 'investor_sponsors', category: 'Investor/Lender/Developer' },
  'Equity': { apiEndpoint: 'investor_sponsors', routeType: 'investor_sponsors', category: 'Investor/Lender/Developer' },
  'Developer(s)': { apiEndpoint: 'investor_sponsors', routeType: 'investor_sponsors', category: 'Investor/Lender/Developer' },
  'Architect of Record': { apiEndpoint: 'consultants', routeType: 'consultants', category: 'Consultant' },
  'Design Architect': { apiEndpoint: 'consultants', routeType: 'consultants', category: 'Consultant' },
  'Civil Engineer': { apiEndpoint: 'consultants', routeType: 'consultants', category: 'Consultant' },
  'Structural Engineer': { apiEndpoint: 'consultants', routeType: 'consultants', category: 'Consultant' },
  'MEP Engineer': { apiEndpoint: 'consultants', routeType: 'consultants', category: 'Consultant' },
  'Geotechnical Engineer': { apiEndpoint: 'consultants', routeType: 'consultants', category: 'Consultant' },
  'Environmental Engineer': { apiEndpoint: 'consultants', routeType: 'consultants', category: 'Consultant' },
  'Interior Design': { apiEndpoint: 'consultants', routeType: 'consultants', category: 'Consultant' },
  'Landscape Architect': { apiEndpoint: 'consultants', routeType: 'consultants', category: 'Consultant' },
  "Owner's Representative": { apiEndpoint: 'consultants', routeType: 'consultants', category: 'Consultant' }
}

const columnDefs = [
  {
    headerName: 'Company', field: 'name', flex: 2, cellRenderer: (p) => {
      if (!p.value) return ''
      const a = document.createElement('a')
      a.href = 'javascript:void(0)'
      a.innerText = p.value
      a.className = 'dir-link'
      a.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const type = p?.data?.routeType
        const id = p?.data?.id
        if (type && id) router.push(`/companies/${type}/${id}`)
      })
      return a
    }
  },
  { headerName: 'Type', field: 'type', flex: 1 },
  { headerName: 'Total Projects', field: 'projectCount', flex: 1, valueFormatter: n => (n.value || 0).toLocaleString() },
  { headerName: 'Total Original Budget', field: 'totalOriginalBudget', flex: 1.2, valueFormatter: n => {
    const val = n.value || 0
    if (val >= 1000000000) return `$${(val/1000000000).toFixed(1)}B`
    if (val >= 1000000) return `$${(val/1000000).toFixed(1)}M`
    if (val >= 1000) return `$${(val/1000).toFixed(0)}K`
    return `$${val.toLocaleString()}`
  }},
  { headerName: 'Avg Budget Variance %', field: 'avgBudgetVariancePct', flex: 1, valueFormatter: n => {
    const val = parseFloat(n.value || 0)
    return `${isNaN(val) ? 0 : val.toFixed(1)}%`
  }},
  // HIDDEN: Schedule Variance column removed per user request
  // { headerName: 'Avg Schedule Variance %', field: 'avgScheduleVariancePct', flex: 1, valueFormatter: n => {
  //   const val = parseFloat(n.value || 0)
  //   return `${isNaN(val) ? 0 : val.toFixed(1)}%`
  // }},
]

function onGridReady(params){ gridApi.value = params.api }

watch(quickFilter, (val) => {
  if (gridApi.value && !gridApi.value.isDestroyed()) {
    if (typeof gridApi.value.setQuickFilter === 'function') {
      gridApi.value.setQuickFilter(val || '')
    } else if (typeof gridApi.value.setGridOption === 'function') {
      gridApi.value.setGridOption('quickFilterText', val || '')
    }
  }
})

// Pagination state
const currentPage = ref(1)
const pageSize = ref(100)
const totalRecords = ref(0)
const isLoading = ref(false)
const optimizedEndpointsAvailable = ref(true)

onMounted(async () => {
  await loadCompaniesOptimized()
})

async function loadCompaniesOptimized() {
  if (isLoading.value) return
  
  isLoading.value = true
  const authHeader = authStore.getAuthTokenHeader

  try {
    // Use single unified endpoint for all company types
    console.log('🔄 Loading companies from unified optimized endpoint...')
    const result = await fetchOptimizedList(`${apiBase}contractors/index_optimized`, authHeader)
    
    if (result && result.companies && Array.isArray(result.companies)) {
      // Process unified company data
      rows.value = result.companies.map(company => {
        // Map company_type to routeType for navigation
        let routeType = 'contractors' // default
        
        // Investor types: Investor/Developer, Lender, Owner, or generic Investor/Lender
        if (['Investor/Developer', 'Lender', 'Owner', 'Investor/Lender'].includes(company.company_type)) {
          routeType = 'investor_sponsors'
        }
        // Consultant types: Architect, Engineer, Interior Design, etc. or generic Consultant
        else if (['Architect', 'Engineer', 'Interior Design', 'Expeditor', 'Legal', 'Environmental', 'Other Consultant', 'Consultant', 'Arch', 'Owner Rep', 'Landscape'].includes(company.company_type)) {
          routeType = 'consultants'
        }
        // Contractor types: GC, Subcontractor, CM, or generic Contractor
        else if (['GC', 'Subcontractor', 'CM', 'Contractor'].includes(company.company_type)) {
          routeType = 'contractors'
        }
        
        return {
          id: company.id,
          name: company.name,
          type: company.company_type,
          routeType: routeType,
          projectCount: company.projects_count || 0,
          totalOriginalBudget: company.total_original_budget || 0,
          avgBudgetVariancePct: company.avg_budget_variance_pct || 0,
          avgScheduleVariancePct: company.avg_schedule_variance_pct || 0,
          last_updated: company.last_updated
        }
      })
      
      // Sort by name
      rows.value.sort((a, b) => a.name.localeCompare(b.name))
      
      optimizedEndpointsAvailable.value = true
      totalRecords.value = rows.value.length
      
      console.log(`✅ Loaded ${rows.value.length} companies (optimized: true)`)
      
      if (result.summary) {
        console.log(`📊 Summary: ${result.summary.contractors} contractors, ${result.summary.investor_sponsors} sponsors, ${result.summary.consultants} consultants`)
      }
    } else {
      throw new Error('Invalid response format from unified endpoint')
    }
    
  } catch (error) {
    console.error('💥 Failed to load optimized data:', error)
    console.log('📊 Falling back to legacy endpoints...')
    
    // Fallback to legacy endpoints
    const combined = []
    
    try {
      const contractors = await fetchList(`${apiBase}contractors`, authHeader)
      pushCompanies(combined, contractors, 'Contractor', 'contractors')
      
      const sponsors = await fetchList(`${apiBase}investor_sponsors`, authHeader)
      pushCompanies(combined, sponsors, 'Investor/Lender/Developer', 'investor_sponsors')
      
      const consultants = await fetchList(`${apiBase}consultants`, authHeader)
      pushCompanies(combined, consultants, 'Consultant', 'consultants')
      
      rows.value = combined
      totalRecords.value = combined.length
      
      console.log(`✅ Loaded ${combined.length} companies (legacy fallback)`)
      
    } catch (legacyError) {
      console.error('❌ Legacy fallback also failed:', legacyError)
      errorStore.setError('Directory', 'Failed to load company directory', 'error', false)
    }
  } finally {
    isLoading.value = false
  }
}

async function fetchOptimizedList(url, headers) {
  try {
    const response = await axiosQuery(url, headers, null, errorStore, 'GET')
    return response
  } catch (error) {
    console.warn(`Optimized endpoint ${url} not available:`, error.message)
    throw error
  }
}

function pushOptimizedCompanies(target, list, typeLabel, routeType) {
  (list || []).forEach(c => {
    target.push({
      id: c.id,
      name: c.name,
      type: typeLabel,
      routeType,
      projectCount: c.projects_count || c.total_projects_count || 0,
      totalOriginalBudget: c.total_original_budget || 0,
      avgBudgetVariancePct: c.avg_budget_variance_pct || c.avg_budget_variance_percent || 0,
      avgScheduleVariancePct: c.avg_schedule_variance_pct || c.avg_schedule_variance_percent || 0,
      lastUpdated: c.last_updated || c.last_stats_updated_at
    })
  })
}

function pushCompanies(target, list, typeLabel, routeType){
  (list || []).forEach(c => {
    target.push({
      id: c.id,
      name: c.name,
      // Use specific company_type or contractor_type if available, fallback to generic typeLabel
      type: c.company_type || c.contractor_type || typeLabel,
      routeType,
      projects_count: c.projects_count,
      project_ids: c.project_ids,
      total_original_budget: c.total_original_budget,
      avg_budget_variance_pct: c.avg_budget_variance_pct,
      avg_schedule_variance_pct: c.avg_schedule_variance_pct,
    })
  })
}

async function fetchList(url, headers){
  return new Promise((resolve) => {
    axiosQuery(url, headers, (data) => resolve(data || []), errorStore)
  })
}

// Add Company Modal Functions
function openAddCompanyModal() {
  showAddCompanyModal.value = true
}

function closeAddCompanyModal() {
  showAddCompanyModal.value = false
  // Reset form
  companyForm.value = {
    name: '',
    company_type: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipcode: '',
    license_number: '',
    website: '',
    notes: ''
  }
}

function updateCompanyType() {
  // This function can be used to show/hide fields based on company type
  console.log('Company type changed to:', companyForm.value.company_type)
}

async function refreshStats() {
  if (isLoading.value) return
  
  try {
    console.log('🔄 Refreshing company statistics...')
    const authHeader = authStore.getAuthTokenHeader
    
    // Only call the contractors refresh_stats endpoint (handles all company types via background job)
    await axiosPostQuery(`${apiBase}contractors/refresh_stats`, {}, authHeader, null, errorStore)
    
    // Reload the page data
    await loadCompaniesOptimized()
    
    console.log('✅ Stats refresh initiated')
    alert('Company statistics refresh initiated. Data will be updated shortly.')
  } catch (error) {
    console.warn('Stats refresh not available:', error.message)
    // Fallback to just reloading data
    await loadCompaniesOptimized()
    alert('Stats refresh not available. Data has been reloaded.')
  }
}

async function saveNewCompany() {
  if (!companyForm.value.name.trim()) {
    alert('Company name is required')
    return
  }
  
  if (!companyForm.value.company_type) {
    alert('Company type is required')
    return
  }

  const typeConfig = COMPANY_TYPE_CONFIG[companyForm.value.company_type]
  if (!typeConfig) {
    alert('Invalid company type selected')
    return
  }

  try {
    console.log('💾 Saving new company:', companyForm.value)
    
    const authHeader = authStore.getAuthTokenHeader
    
    // Prepare payload based on company type
    let payload = {
      name: companyForm.value.name,
      email: companyForm.value.email,
      phone: companyForm.value.phone,
      address_line_1: companyForm.value.address,
      city: companyForm.value.city,
      state: companyForm.value.state,
      zipcode: companyForm.value.zipcode,
      website: companyForm.value.website,
      notes: companyForm.value.notes
    }
    
    // Add type-specific fields
    if (typeConfig.apiEndpoint === 'contractors') {
      payload.contractor_type = companyForm.value.company_type
      payload.license_number = companyForm.value.license_number
    } else if (typeConfig.apiEndpoint === 'investor_sponsors') {
      payload.investment_role = companyForm.value.company_type
    } else if (typeConfig.apiEndpoint === 'consultants') {
      payload.consultant_type = companyForm.value.company_type
    }
    
    // Wrap payload based on what each backend controller expects
    let wrappedPayload = payload
    if (typeConfig.apiEndpoint === 'consultants') {
      // Consultants controller uses params.require(:consultant)
      wrappedPayload = { consultant: payload }
    } else if (typeConfig.apiEndpoint === 'contractors') {
      // Contractors controller uses params.permit (no wrapper needed)
      wrappedPayload = payload
    } else if (typeConfig.apiEndpoint === 'investor_sponsors') {
      // Investor Sponsors controller uses params.require(:investor_sponsor)
      wrappedPayload = { investor_sponsor: payload }
    }
    
    console.log('📦 Payload for ' + typeConfig.apiEndpoint + ':', wrappedPayload)
    
    // Use axiosPostQuery for POST requests (axiosQuery only handles GET)
    const response = await axiosPostQuery(
      `${apiBase}${typeConfig.apiEndpoint}`,
      wrappedPayload,
      authHeader,
      null,
      errorStore,
      `Create ${typeConfig.category}`
    )
    
    if (response) {
      console.log('✅ Company saved successfully:', response)
      
      // Extract the actual new company from response
      // Backend sometimes returns array, sometimes object
      let newCompanyData = response
      if (Array.isArray(response)) {
        console.log('⚠️ Backend returned array instead of single company, finding new one...')
        // Find the company with matching name (most recently added)
        newCompanyData = response.find(c => c.name === companyForm.value.name)
        if (newCompanyData) {
          console.log('✅ Found new company in response:', newCompanyData)
        }
      }
      
      // Close modal first
      closeAddCompanyModal()
      
      // Reload the full company list from the database to include the new company
      console.log('🔄 Reloading company list to include new company...')
      await loadCompaniesOptimized()
      
      // Alert after the data is refreshed
      alert(`Company "${companyForm.value.name}" added successfully!`)
    }
  } catch (error) {
    console.error('❌ Error saving company:', error)
    alert('Error saving company: ' + (error.message || 'Unknown error'))
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/colors.scss";
.directory-view { padding: 24px; max-width: 1400px; margin: 60px auto 0; }
.dir-header { background: #152742; color: #fff; padding: 12px 16px; border-radius: 8px; font-weight: 600; font-size: 16px; letter-spacing: 0.2px; }
.page-sub { color: #6b7280; margin: 10px 0 14px; font-size: 12px; }
.toolbar { 
  margin: 14px 0 14px; 
  display: flex; 
  justify-content: space-between; 
  align-items: center;
  
  input { 
    width: 320px; 
    padding: 8px 10px; 
    border: 1px solid #e5e7eb; 
    border-radius: 6px; 
    box-shadow: 0 1px 2px rgba(0,0,0,0.03); 
  }
  
  .toolbar-actions {
    display: flex;
    gap: 12px;
  }
  
  .refresh-btn {
    background: #10b981;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
    
    &:hover:not(:disabled) {
      background: #059669;
    }
    
    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  }
  
  .add-company-btn {
    background: #4A5FD5;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
    
    &:hover {
      background: #3d4fd5;
    }
  }
}
.grid-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; box-shadow: 0 1px 4px rgba(0,0,0,0.04); margin-top: 6px; }
.directory-grid { height: 68vh; width: 100%; }
.directory-grid.ag-theme-alpine { 
  /* Light gray header for contrast */
  --ag-header-background-color: #f3f4f6;
  --ag-header-foreground-color: #374151;
  --ag-header-border-color: #e5e7eb;
}
:deep(.directory-grid .ag-header) { background-color: #f3f4f6; }
.dir-link { color: #4A5FD5; text-decoration: none; }

/* Modal Styles */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.add-company-modal {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 700px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e5e5;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #374151;
  }
  
  .close-button {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #6b7280;
    
    &:hover {
      color: #374151;
    }
  }
}

.modal-body {
  padding: 24px;
}

.form-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.form-group {
  flex: 1;
  
  &.full-width {
    flex: none;
    width: 100%;
  }
  
  label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #374151;
    font-size: 14px;
  }
}

.form-input, .form-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #4A5FD5;
    box-shadow: 0 0 0 3px rgba(74, 95, 213, 0.1);
  }
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e5e5e5;
}

.btn-secondary, .btn-primary {
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
  
  &:hover {
    background: #e5e7eb;
  }
}

.btn-primary {
  background: #4A5FD5;
  color: white;
  
  &:hover:not(:disabled) {
    background: #3d4fd5;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
}

/* Loading and Status UI */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6b7280;
  
  .loading-spinner {
    font-size: 24px;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.grid-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  
  .optimized-badge {
    background: #10b981;
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6b7280;
  
  .retry-btn {
    margin-top: 16px;
    background: #4A5FD5;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    
    &:hover {
      background: #3d4fd5;
    }
  }
}
</style>